import * as React from "react";
import {AppTranslations} from "../../../components/Utils/translations/AppTranslations";
import {
    AlarmSeverity,
    AlarmStatus,
    JSONAlarmCategoryStatistics,
    JSONAlarmSeverityCount,
    JSONAlarmStatusCount,
} from "../data/AlarmsMonitorData";
import {AlarmsMonitorDataMapper} from "../utils/AlarmsMonitorDataMapper";
import {AlarmMonitorCircle} from "./AlarmMonitorCircle";
import {MonitorAlarmsList} from "./MonitorAlarmsList";
import {Buttons} from "../../../components/buttons/Buttons";
import ButtonWithIcon = Buttons.ButtonWithIcon;
import {VeBoxCard} from "../../../components/card/VeBoxCard";
import {Display} from "../../../components/Display";

declare var AppTranslations: AppTranslations;

interface MonitorWidgetProps {
    data: JSONAlarmCategoryStatistics;
    onListElementClick: (alarmId: number, equipmentId: number) => void;
    selectedSeverity: AlarmSeverity;
    selectedAlarmStatus: AlarmStatus;
    maxAlarmsCount: Map<string, number>;
    onTypesFiltersChange;
    isFilterEnabled: boolean;
    onFilterOpen: (category: JSONAlarmCategoryStatistics) => void;
    openedCategoryId: number;
    openedSeverityGuid: string;
    onAlarmListOpen: (categoryId: number, severityGuid: string, categoryGuid: string) => void;
    onAlarmListClose: () => void;
    filters: string;
    useAsFilter: () => void;
}

interface MonitorWidgetStatus {
    isListOpened: boolean;
}

export class MonitorWidget extends React.Component<MonitorWidgetProps, MonitorWidgetStatus> {

    private data: JSONAlarmStatusCount;

    constructor(props) {
        super(props);
        this.onAlarmsListOpen = this.onAlarmsListOpen.bind(this);
        this.onFilterClick = this.onFilterClick.bind(this);
    }

    public render() {
        if (this.props.data == null) {
            return null;
        }
        const statusData: JSONAlarmStatusCount = this.getStatusData(this.props.data);
        if (this.isStatusDataEmpty(statusData)) {
            return this.renderEmptyWidget();
        }
        const entries = this.props.maxAlarmsCount.entries();
        let maxVal = 0;
        let el = entries.next();
        while (!el.done) {
            if (el.value[1] > maxVal) {
                maxVal = el.value[1];
            }
            el = entries.next();
        }
        let maxInCategory = 0;
        for (const sev of this.data.severitiesCount) {
            if (sev.count > maxInCategory) {
                maxInCategory = sev.count;
            }
        }
        const widgetContent = this.data.severitiesCount.map(
            (element, index) => {
                const severity = AlarmsMonitorDataMapper.toAlarmsSeverityEnum(element.severityGuid);
                return (
                    <AlarmMonitorCircle
                        key={index}
                        alarmStatus={AlarmsMonitorDataMapper.toAlarmsStatusEnum(statusData.statusId)}
                        alarmSeverity={severity}
                        maxValueInSeverity={this.props.maxAlarmsCount.get(element.severityGuid)}
                        maxValueInStatus={maxVal}
                        maxValuesInCategory={maxInCategory}
                        value={element.count}
                        color={
                            AlarmsMonitorDataMapper.getSeverityColor(
                                AppTranslations.alarmsMonitor.severities, element.severityGuid,
                            )
                        }
                        onClick={this.onAlarmsListOpen}
                        borderColor={
                            AlarmsMonitorDataMapper.getSeverityBorderColor(
                                AppTranslations.alarmsMonitor.severities, element.severityGuid,
                            )
                        }
                        index={index}
                        openedAlarmSeverity={
                            (this.props.openedCategoryId === this.props.data.categoryId
                                && this.props.openedSeverityGuid === element.severityGuid) ? severity : null
                        }
                        isCategoryOpen={this.props.openedCategoryId === this.props.data.categoryId}
                    />
                );
            },
        );
        return (
            <div className={"monitor-widget-wrapper"}>
                <VeBoxCard className={"monitor-widget"} style={this.getWidgetStyle()}>
                    <VeBoxCard.Header
                        className={"monitor-widget__header space-between"}
                        title={this.props.data.categoryName}
                        titleClassName={"lowercase capitalize"}
                        singleLineTitle={true}
                        back={this.renderFilter()}
                    />
                    {/*<div className={"react-alarm-separator"}/>*/}
                    <VeBoxCard.Body className={"monitor-widget__content"}>
                        {widgetContent}
                    </VeBoxCard.Body>
                </VeBoxCard>
                <Display isVisible={this.props.openedCategoryId === this.props.data.categoryId}>
                    <MonitorAlarmsList
                        categoryId={this.props.openedCategoryId}
                        severityGuid={this.props.openedSeverityGuid}
                        isOpened={this.props.openedCategoryId === this.props.data.categoryId}
                        onListElementClick={this.props.onListElementClick}
                        onCloseList={this.props.onAlarmListClose}
                        filters={this.props.filters}
                        status={this.props.selectedAlarmStatus}
                        useAsFilter={this.props.useAsFilter}
                    />
                </Display>
            </div>
    );
    }

    private onAlarmsListOpen(e: React.MouseEvent<HTMLDivElement, MouseEvent>): void {
        // tslint:disable-next-line:radix
        const severityGuid: string = e.currentTarget.dataset.id;
        this.props.onAlarmListOpen(this.props.data.categoryId, severityGuid, this.props.data.categoryGuid);
    }

    private renderFilter() {
        return <ButtonWithIcon
            key="monitorHeaderFilterButton"
            code="monitorHeaderFilterButton"
            icon={this.props.isFilterEnabled ? "images/buttons/icon-filter-filled.png" : "images/buttons/icon-filter.png"}
            invertIconColor={true}
            variant={"tertiary"}
            height="25px"
            width="25px"
            onClick={this.onFilterClick}
        />
    }

    private onFilterClick() {
        this.props.onFilterOpen(this.props.data);
    }

    private getWidgetStyle() {
        let height = "210px";
        if (this.props.openedCategoryId === this.props.data.categoryId) {
            height = "110px";
        }
        if (this.props.openedCategoryId != null && this.props.openedCategoryId !== this.props.data.categoryId) {
            return {opacity: 0.3, height};
        } else if (this.props.openedCategoryId != null) {
            return {opacity: 1, /*backgroundColor: "#efefef",*/ height};
        }
        return {opacity: 1, height};
    }

    private renderEmptyWidget() {
        return (
            <VeBoxCard className={"monitor-widget"} style={this.getWidgetStyle()}>
                <VeBoxCard.Header
                    className={"monitor-widget__header space-between"}
                    title={this.props.data.categoryName}
                    titleClassName={"lowercase capitalize"}
                    back={this.renderFilter()}
                    singleLineTitle={true}
                />
                {/*<div className={"react-alarm-separator"}/>*/}
                <VeBoxCard.Body className={"monitor-widget__content"}>
                    <div className={"no-alarm__circle"}>
                        <div className={"no-alarm__inner-circle"}>
                            <p>{AppTranslations.alarmsMonitor.texts.noAlarm}</p>
                        </div>
                    </div>
                </VeBoxCard.Body>
            </VeBoxCard>
        );
    }

    private isStatusDataEmpty(data: JSONAlarmStatusCount) {
        if (data == null || data.count === 0) {
            return true;
        }
        if (this.props.selectedSeverity != null) {
            let severityCount: JSONAlarmSeverityCount = null;
            data.severitiesCount.forEach(
                (severity) => {
                    if (AlarmsMonitorDataMapper
                        .toAlarmsSeverityEnum(severity.severityGuid) === this.props.selectedSeverity) {
                        severityCount = severity;
                    }
                },
            );
            if (severityCount == null || severityCount.count === 0) {
                return true;
            }
            this.data = {
                statusId: data.statusId,
                count: severityCount.count,
                severitiesCount: [severityCount],
            };
        } else {
            this.data = data;
        }
        return false;
    }

    private getStatusData(data: JSONAlarmCategoryStatistics): JSONAlarmStatusCount {
        if (data == null || data.statusStatistics == null || data.statusStatistics.length === 0) {
            return null;
        }
        for (const status of data.statusStatistics) {
            if (AlarmsMonitorDataMapper.toAlarmsStatusEnum(status.statusId) === this.props.selectedAlarmStatus) {
                return status;
            }
        }
        return null;
    }
}
